/** A lane's weekly WIG session: Account, Scoreboard, Commit.
 *
 * The book's twenty minutes, in its order, full screen so the whirlwind stays out of the
 * room. Each step is what the book says it is and nothing else:
 *
 * 1. **Account** — each person marks last week's commitment kept or missed. No discussion
 *    field on purpose: "done or not done, no explanation" is the mechanism.
 * 2. **Scoreboard** — the numbers, read aloud. Last week's lead counts can still be
 *    corrected here, because this is when people remember them.
 * 3. **Commit** — each person names what they will do this week. Nobody can type another
 *    person's commitment; the API refuses it too.
 */

import { useEffect, useState } from 'react'
import { Check, X } from 'lucide-react'

import { Button, ErrorState } from '@/components/ui'
import { CommitForm, CommitmentItem, GoalLine } from '@/features/wig/WigLane'
import { HealthChip, ScoreBar, Tally } from '@/features/wig/WigParts'
import {
  formatNumber,
  previousWeek,
  towards,
  useRecordEntry,
  useUpdateCommitment,
  type Commitment,
  type WigLane,
} from '@/features/wig/useWig'

const STEPS = ['Account', 'Scoreboard', 'Commit'] as const

function AccountRow({ commitment }: { commitment: Commitment }) {
  const update = useUpdateCommitment()
  const set = (status: Commitment['status']) =>
    update.mutate({ id: commitment.id, status, row_version: commitment.rowVersion })
  return (
    <li className="flex flex-wrap items-center gap-3 py-3">
      <div className="min-w-0 flex-1">
        <p className="text-lg text-fg">{commitment.text}</p>
        <p className="text-sm text-fg-muted">{commitment.person.displayName}</p>
      </div>
      {commitment.mine ? (
        <div className="flex gap-2">
          <Button
            variant={commitment.status === 'kept' ? 'primary' : 'secondary'}
            onClick={() => set(commitment.status === 'kept' ? 'open' : 'kept')}
            disabled={update.isPending}
            aria-pressed={commitment.status === 'kept'}
          >
            <Check size={15} aria-hidden />
            Kept
          </Button>
          <Button
            variant={commitment.status === 'missed' ? 'destructive' : 'secondary'}
            onClick={() => set(commitment.status === 'missed' ? 'open' : 'missed')}
            disabled={update.isPending}
            aria-pressed={commitment.status === 'missed'}
          >
            <X size={15} aria-hidden />
            Missed
          </Button>
        </div>
      ) : (
        <span
          className="text-sm font-semibold"
          style={{
            color:
              commitment.status === 'kept'
                ? 'var(--status-green)'
                : commitment.status === 'missed'
                  ? 'var(--status-red)'
                  : 'var(--fg-muted)',
          }}
        >
          {commitment.status === 'open' ? 'Waiting for them' : commitment.status === 'kept' ? 'Kept' : 'Missed'}
        </span>
      )}
      {update.isError ? (
        <span className="w-full text-xs" style={{ color: 'var(--status-red)' }}>
          {update.error instanceof Error ? update.error.message : 'Not saved'}
        </span>
      ) : null}
    </li>
  )
}

function Scoreboard({ lane }: { lane: WigLane }) {
  const record = useRecordEntry()
  const lastWeek = previousWeek(lane.weekStart)
  return (
    <div className="space-y-8">
      <section>
        <p className="micro-label mb-3 text-fg-muted">Lag — the result</p>
        <ul className="space-y-6">
          {lane.lag.map((measure) => (
            <li key={measure.id}>
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <span className="text-lg text-fg">{measure.name}</span>
                <span className="font-heading text-5xl leading-none text-fg">
                  {formatNumber(measure.current)}
                  <span className="text-2xl text-fg-muted">
                    {' '}
                    {towards(measure)} {measure.unit}
                  </span>
                </span>
              </div>
              <div className="mt-3">
                <ScoreBar lag={measure} tall />
              </div>
              <p className="mt-2 text-sm text-fg-muted">
                Pace says {formatNumber(measure.expected)} by today.
              </p>
            </li>
          ))}
        </ul>
      </section>
      <section>
        <p className="micro-label mb-3 text-fg-muted">Lead — last week, and this week so far</p>
        <ul className="space-y-6">
          {lane.lead.map((measure) => {
            const target = measure.weeklyTarget ?? 0
            const met = measure.lastWeek >= target
            return (
              <li key={measure.id} className="grid gap-3 md:grid-cols-[1fr_auto_auto] md:items-center">
                <span className="text-lg text-fg">
                  {measure.name}
                  <span className="block text-sm text-fg-muted">{formatNumber(target)} a week</span>
                </span>
                <div>
                  <p className="micro-label mb-1 text-fg-muted">
                    Last week{' '}
                    <span style={{ color: met ? 'var(--status-green)' : 'var(--status-red)' }}>
                      {met ? '· met' : '· short'}
                    </span>
                  </p>
                  <Tally
                    big
                    count={measure.lastWeek}
                    target={target}
                    disabled={!lane.canRecord || record.isPending}
                    onSet={
                      lane.canRecord
                        ? (next) => record.mutate({ measureId: measure.id, value: next, week: lastWeek })
                        : undefined
                    }
                  />
                </div>
                <div>
                  <p className="micro-label mb-1 text-fg-muted">This week</p>
                  <Tally
                    count={measure.thisWeek}
                    target={target}
                    disabled={!lane.canRecord || record.isPending}
                    onSet={lane.canRecord ? (next) => record.mutate({ measureId: measure.id, value: next }) : undefined}
                  />
                </div>
              </li>
            )
          })}
        </ul>
        {record.isError ? (
          <div className="mt-3">
            <ErrorState error={record.error} />
          </div>
        ) : null}
      </section>
    </div>
  )
}

export function WigSession({ lane, onClose }: { lane: WigLane; onClose: () => void }) {
  const [step, setStep] = useState(0)

  // Escape leaves the session, and only the session.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      onClose()
    }
    window.addEventListener('keydown', handler, true)
    return () => window.removeEventListener('keydown', handler, true)
  }, [onClose])

  const last = lane.commitments.lastWeek
  return (
    <div className="fixed inset-0 z-[60] overflow-auto bg-canvas" role="dialog" aria-modal="true" aria-label={`WIG session: ${lane.projectName}`}>
      <div className="mx-auto max-w-4xl px-6 py-8">
        <div className="flex flex-wrap items-start gap-4">
          <div className="min-w-0 flex-1">
            <p className="micro-label text-fg-muted">WIG session · {lane.projectName}</p>
            <div className="mt-2">
              <GoalLine lane={lane} big />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <HealthChip health={lane.health} large />
            <span className="text-center">
              <span className="block font-heading text-5xl leading-none text-fg">{lane.fridaysLeft ?? '—'}</span>
              <span className="micro-label text-fg-muted">Fridays left</span>
            </span>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-2 text-fg-muted hover:bg-surface"
              aria-label="Leave the session"
            >
              <X size={20} aria-hidden />
            </button>
          </div>
        </div>

        <ol className="mt-8 flex gap-2" aria-label="Session steps">
          {STEPS.map((label, index) => (
            <li key={label} className="flex-1">
              <button
                type="button"
                onClick={() => setStep(index)}
                aria-current={index === step ? 'step' : undefined}
                className="w-full rounded-md border px-3 py-2 text-left text-sm font-semibold"
                style={
                  index === step
                    ? { backgroundColor: 'var(--brand)', color: 'var(--brand-on)', borderColor: 'var(--brand)' }
                    : { borderColor: 'var(--border)', color: 'var(--fg)' }
                }
              >
                {index + 1}. {label}
              </button>
            </li>
          ))}
        </ol>

        <div className="card mt-6 p-6">
          {step === 0 ? (
            <>
              <h2 className="font-heading text-2xl text-fg">Last week's commitments</h2>
              <p className="mt-1 text-sm text-fg-muted">
                Done or not done — each person marks their own. No explanations; the
                discomfort is the mechanism.
              </p>
              {last.length ? (
                <>
                  <ul className="mt-4 divide-y divide-border">
                    {last.map((c) => (
                      <AccountRow key={c.id} commitment={c} />
                    ))}
                  </ul>
                  <p className="mt-4 text-sm text-fg-muted">
                    Kept so far: <b className="text-fg">{lane.commitments.lastWeekKept} of {lane.commitments.lastWeekTotal}</b>
                  </p>
                </>
              ) : (
                <p className="mt-4 text-sm text-fg-muted">
                  Nothing was committed last week, so there is nothing to account for. That is
                  worth saying out loud in the room.
                </p>
              )}
            </>
          ) : step === 1 ? (
            <>
              <h2 className="mb-5 font-heading text-2xl text-fg">Are we winning?</h2>
              <Scoreboard lane={lane} />
            </>
          ) : (
            <>
              <h2 className="font-heading text-2xl text-fg">This week</h2>
              <p className="mt-1 text-sm text-fg-muted">
                Each person names one or two things that move a lead measure. They name it —
                nobody names it for them.
              </p>
              <ul className="mt-4 space-y-2">
                {lane.commitments.thisWeek.map((c) => (
                  <CommitmentItem key={c.id} commitment={c} />
                ))}
              </ul>
              {lane.canCommit ? (
                <div className="mt-4">
                  <CommitForm lane={lane} />
                </div>
              ) : (
                <p className="mt-4 text-sm text-fg-muted">
                  Commitments come from the lane's owner and supporters.
                </p>
              )}
            </>
          )}
        </div>

        <div className="mt-6 flex justify-between">
          <Button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}>
            Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button variant="primary" onClick={() => setStep((s) => s + 1)}>
              Next: {STEPS[step + 1]}
            </Button>
          ) : (
            <Button variant="primary" onClick={onClose}>
              End the session
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
